import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Highlight, { defaultProps } from 'prism-react-renderer'
import rangeParser from 'parse-numeric-range'
import theme from 'prism-react-renderer/themes/dracula'
import { LiveProvider, LiveEditor } from 'react-live'
import * as mermaid from 'mermaid'

import { copyToClipboard } from '@rocketseat/gatsby-theme-docs/src/util/copy-to-clipboard'
import scope from '@rocketseat/gatsby-theme-docs/src/components/Code/LiveCodeScope'
import {
  CopyCode,
  LineNo,
  Pre,
  PreHeader,
  LiveWrapper,
  LivePreview,
  LiveError,
  StyledEditor,
} from '@rocketseat/gatsby-theme-docs/src/components/Code/styles'

import Prism from 'prism-react-renderer/prism'
;(typeof global !== 'undefined' ? global : window).Prism = Prism

require('prismjs/components/prism-solidity')

const calculateLinesToHighlight = (meta) => {
  const RE = /{([\d,-]+)}/

  if (RE.test(meta)) {
    const strlineNumbers = RE.exec(meta)[1]
    const lineNumbers = rangeParser(strlineNumbers)
    return (index) => lineNumbers.includes(index + 1)
  }

  return () => false
}

const PIE_COLORS = {
  Informational: '#a287e6',
  Minor: '#ffd34b',
  Medium: '#ff7635',
  Major: '#fb371d',
  Critical: '#a51835',
}

export default function CodeHighlight({
  codeString,
  className,
  live,
  highlight,
  title,
  lineNumbers,
  lineOffset,
  special,
}) {
  const [copied, setCopied] = useState(false)
  const language = className && className.replace(/language-/, '')

  const shouldHighlightLine = calculateLinesToHighlight(highlight)

  const handleClick = () => {
    setCopied(true)
    copyToClipboard(codeString)

    setTimeout(() => {
      setCopied(false)
    }, 4000)
  }

  if (language === 'mermaid') {
    let uuid = '_' + Math.random().toString(36).substr(2, 9)

    const toClear = setInterval(() => {
      const el = document.getElementById(`hash${uuid}`)

      if (el !== null) {
        uuid = undefined;

        clearInterval(toClear);

        mermaid.init(undefined, el);

        // Fix overflow of legend
        el.querySelector("svg").style.overflow = "visible";

        // Retrieve all severities
        const legends = el.querySelectorAll(".legend text");

        // Split rows for count of issues
        const codeRows = codeString.split("\n");

        const severityFindings = codeRows
          .filter(
            (r) =>
              r.indexOf(": 0") === -1 && r.replace(/[^0-9]*/g, "").length !== 0
          )
          .map((row) => {
            const severity = row
              .slice(row.indexOf('"') + 1, row.lastIndexOf('"'))
              .toLowerCase();
            const findings = row.replace(/[^0-9]*/g, "");
            const r = {};
            r[severity] = findings;
            return r;
          });

        const totalFindings = severityFindings.reduce(
          (acc, v) => acc + parseInt(Object.values(v)[0]),
          0
        );

        const percentageToFindings = severityFindings.reduce((acc, v) => {
          const s = Object.keys(v)[0];
          const percentage = Math.round((v[s] / totalFindings) * 100) + "%";
          acc[percentage] = v[s];
          return acc;
        }, {});

        // Update Colors
        const colorSlices = el.querySelectorAll("path");
        const colorSquares = el.querySelectorAll("rect");
        colorSlices.forEach((slice, i) => {
          // Use attribute style setting for pie slice color
          const fill = document.createAttribute("fill");

          fill.value = PIE_COLORS[legends[i].textContent];
          slice.attributes.setNamedItem(fill);

          // Adjust style for square legend color
          const square = colorSquares[i];
          square.style.fill = PIE_COLORS[legends[i].textContent];
          square.style.stroke = PIE_COLORS[legends[i].textContent];

          const totalOfSeverity = Object.values(
            severityFindings.find(
              (v) => Object.keys(v)[0] === legends[i].textContent.toLowerCase()
            ) || { "": 0 }
          )[0];
          if (totalOfSeverity !== 0)
            legends[i].textContent += ` (${Math.round(
              (totalOfSeverity / totalFindings) * 100
            )}%)`;
        });

        // Update text within legend and each slice
        el.querySelectorAll(".slice").forEach((slice, i) => {
          const percentage = slice.textContent;
          slice.textContent = percentageToFindings[percentage];
        });
      }
    }, 1000)

    return <div id={`hash${uuid}`}>{codeString}</div>
  }

  if (live) {
    return (
      <LiveProvider
        code={codeString}
        noInline
        theme={theme}
        transformCode={(code) => `/** @jsx mdx */${code}`}
        scope={scope}
      >
        <LiveWrapper>
          <LivePreview />

          <StyledEditor>
            <CopyCode onClick={handleClick} disabled={copied} hasTitle>
              {copied ? 'Copied!' : 'Copy'}
            </CopyCode>

            <LiveEditor />
          </StyledEditor>

          <LiveError />
        </LiveWrapper>
      </LiveProvider>
    )
  }

  return (
    <>
      {title && <PreHeader>{title}</PreHeader>}
      <div className="gatsby-highlight">
        <Highlight
          {...defaultProps}
          code={codeString}
          language={language}
          theme={theme}
        >
          {({
            className: blockClassName,
            style,
            tokens,
            getLineProps,
            getTokenProps,
          }) => (
            <Pre
              className={blockClassName}
              style={style}
              hasTitle={title}
              hasLanguage={!!language}
            >
              <CopyCode
                onClick={handleClick}
                disabled={copied}
                hasTitle={title}
              >
                {copied ? 'Copied!' : 'Copy'}
              </CopyCode>
              <code>
                {tokens.map((line, index) => {
                  const lineProps = getLineProps({ line, key: index })

                  if (shouldHighlightLine(index)) {
                    lineProps.className = `${lineProps.className} highlight-line`
                  }

                  return (
                    <div {...lineProps}>
                      {lineNumbers && (
                        <LineNo>{parseInt(lineOffset) + index + 1}</LineNo>
                      )}
                      {line.map((token, key) => (
                        <span {...getTokenProps({ token, key })} />
                      ))}
                    </div>
                  )
                })}
              </code>
            </Pre>
          )}
        </Highlight>
      </div>
    </>
  )
}

CodeHighlight.propTypes = {
  codeString: PropTypes.string.isRequired,
  className: PropTypes.string.isRequired,
  highlight: PropTypes.string,
  live: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  title: PropTypes.string,
  lineNumbers: PropTypes.string,
  lineOffset: PropTypes.number,
  special: PropTypes.bool,
}

CodeHighlight.defaultProps = {
  live: false,
  title: null,
  lineNumbers: null,
  lineOffset: null,
  highlight: 0,
}
